import { useMemo } from 'react';

import { useFormState, useForm } from 'react-final-form';
import { SelectedInstanceType } from '@cast/types';

import {
  ControlWithDescription,
  InlineWithDescription,
} from 'components/form-layouts';

import { ProvisionNodesCloudConfigurationFormValues } from '../../types';

import { RffSelect } from '@cast/shared-rff-ui';

import { Stack } from '@cast/design-system';

type GpuConfigurationSectionProps = {
  availableGpuTypes: string[];
  availableInstances: SelectedInstanceType[];
  gpuSettingsDisabled: boolean;
  gpuTypeHint?: string;
  gpuCountHint?: string;
};

const NO_GPU_TYPES_AVAILABLE = 'No GPU types available';
const NO_GPU_COUNT_AVAILABLE = 'No GPU count available';

export const GpuConfigurationSection = ({
  availableGpuTypes,
  availableInstances,
  gpuSettingsDisabled,
  gpuTypeHint,
  gpuCountHint,
}: GpuConfigurationSectionProps) => {
  const form = useForm();
  const { values } = useFormState<ProvisionNodesCloudConfigurationFormValues>({
    subscription: { values: true },
  });

  const { instanceType, availabilityZone, gpuType } = values;

  const handleGpuTypeChange = () => {
    form.batch(() => {
      form.change('gpuCount', undefined);
      form.change('replicas', undefined);
      form.change('partitionSize', undefined);
    });
  };
  const availableGpuCount = useMemo(() => {
    if (!instanceType || !availabilityZone || !gpuType) {
      return [];
    }

    return Array.from(
      new Set(
        availableInstances
          .filter((i) => i.name === instanceType)
          .filter((i) => i.zone === availabilityZone)
          .map((i) => i.availableGpuDevices)
          .flat()
          .filter((d) => d?.name === gpuType)
          .map((id) => id?.count)
      )
    );
  }, [availableInstances, instanceType, availabilityZone, gpuType]);

  return (
    <InlineWithDescription
      description={
        <Stack gap={4}>
          <ControlWithDescription label="GPU configuration">
            Creating GCP NI with customer quantity and type of GPUs attached.
            Eg.: n1-standard-2 with 8 x NVIDIA Tesla K80
          </ControlWithDescription>
        </Stack>
      }
      controls={
        <Stack gap={16}>
          <RffSelect
            name="gpuType"
            label="GPU type"
            inputProps={{
              placeholder: 'Select GPU type',
              hint: gpuTypeHint,
            }}
            options={
              availableGpuTypes.length > 0
                ? availableGpuTypes
                : [NO_GPU_TYPES_AVAILABLE]
            }
            disabled={gpuSettingsDisabled}
            optionDisabled={(option) => option === NO_GPU_TYPES_AVAILABLE}
            onChange={handleGpuTypeChange}
            testId="gpu-type-select"
          />
          <RffSelect
            name="gpuCount"
            label="GPU count"
            inputProps={{
              placeholder: 'Select GPU count',
              hint: gpuCountHint,
            }}
            options={
              availableGpuCount.length > 0
                ? availableGpuCount.map((count) => ({
                    value: count,
                    label: String(count),
                  }))
                : [{ value: 0, label: NO_GPU_COUNT_AVAILABLE }]
            }
            optionValue="value"
            optionLabel="label"
            disabled={gpuSettingsDisabled || !gpuType}
            optionDisabled={(option) => option.label === NO_GPU_COUNT_AVAILABLE}
            testId="gpu-count-select"
          />
        </Stack>
      }
    />
  );
};
